import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, X, Bot, HelpCircle } from 'lucide-react';
import { getChatResponse } from '../services/geminiService';

interface ChatMessage {
  role: 'user' | 'model';
  content: string;
}

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'model',
      content:
        "Hello — I'm the Machete Man assistant. Ask me about blade types, bulk pricing, or shipping times.",
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  // Escape closes the panel
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage: ChatMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const historyForApi = messages.map((m) => ({ role: m.role, text: m.content }));
      // @ts-ignore — geminiService accepts simple {role, text} format
      const responseText = await getChatResponse(input, historyForApi);
      setMessages((prev) => [...prev, { role: 'model', content: responseText }]);
    } catch (error) {
      console.error('Chat Error:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          content:
            "I'm having trouble connecting to the warehouse right now. Please try again later.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Toggle */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-forge hover:bg-ember text-paper-warm p-3.5 rounded-full shadow-raise hover:shadow-float transition-colors duration-200 ease-editorial"
          aria-label="Open chat"
        >
          <MessageSquare className="w-5 h-5" strokeWidth={1.5} />
        </button>
      )}

      {/* Window */}
      {/* Window — height/width capped to the viewport so the header (and its
          close button) can never be pushed off-screen on short windows. */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[min(400px,calc(100vw-3rem))] h-[min(540px,calc(100vh-6rem))] bg-paper-bright shadow-float flex flex-col border border-ink-200 overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300 ease-editorial">

          {/* Header */}
          <header className="bg-forge px-5 py-4 flex justify-between items-center text-paper">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-forge-deep border border-paper/10 flex items-center justify-center">
                <Bot className="w-4 h-4 text-ember-soft" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-editorial italic text-base text-paper-warm leading-tight">
                  Machete support
                </p>
                <p className="font-mono text-[10px] tracking-micro uppercase text-paper/55 flex items-center mt-0.5">
                  <span className="w-1.5 h-1.5 bg-olive rounded-full mr-1.5" />
                  Online
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-paper/60 hover:text-paper transition-colors duration-150 p-2.5 -m-1"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </header>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-paper">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[82%] px-3.5 py-2.5 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-ember text-paper-warm'
                      : 'bg-paper-bright text-ink-900 border border-ink-200'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-paper-bright border border-ink-200 px-4 py-3 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-ink-300 rounded-full animate-bounce" />
                  <span className="w-1.5 h-1.5 bg-ink-300 rounded-full animate-bounce [animation-delay:120ms]" />
                  <span className="w-1.5 h-1.5 bg-ink-300 rounded-full animate-bounce [animation-delay:240ms]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            className="p-4 bg-paper-bright border-t border-ink-200"
          >
            <div className="relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask a question..."
                className="w-full bg-paper border border-ink-200 pl-3.5 pr-12 py-3 text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-ember focus:ring-1 focus:ring-ember/20 transition-colors duration-150"
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 bg-ember hover:bg-ember-deep text-paper-warm disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150"
                aria-label="Send"
              >
                <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
            <p className="font-mono text-[9px] tracking-micro uppercase text-ink-500 mt-3 inline-flex items-center justify-center w-full gap-1.5">
              <HelpCircle className="w-2.5 h-2.5" strokeWidth={1.5} />
              AI can make mistakes · verify specifics
            </p>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBot;
